import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, PhoneCall, ArrowRight, ChatCircleText } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { tokenHeader, track } from "../../lib/clarity";
import CallbackForm from "./CallbackForm";

const URGENCY_TONE = { "Near-term": "border-orange-300/60 !text-orange-300", Soon: "border-exodus-gold/60 !text-exodus-gold", "When ready": "border-exodus-border !text-exodus-muted" };

export function ActionPlan({ actions }) {
  return (
    <section data-testid="report-action-plan" className="card-surface p-6 sm:p-10">
      <p className="eyebrow">Prioritized action plan</p>
      <h3 className="font-display text-3xl mt-2 mb-2">Your next practical steps</h3>
      <p className="text-xs text-exodus-muted mb-8">Ordered by urgency and relevance to your answers — not by any product.</p>
      <ol className="space-y-5">
        {actions.map((a, i) => (
          <li key={a.action} data-testid={`report-action-${i}`} className="flex gap-5">
            <span className="font-display text-3xl gold-text leading-none w-8 shrink-0">{i + 1}</span>
            <div className="flex-1 border-b border-exodus-border pb-5">
              <span className={`inline-block text-[10px] eyebrow border px-2 py-0.5 mb-2 ${URGENCY_TONE[a.urgency]}`}>{a.urgency}</span>
              <p className="text-exodus-text">{a.action}</p>
              {a.reason && <p className="text-xs text-exodus-muted mt-1">{a.reason}</p>}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function ReportNextSteps({ data, token, onNeedContact }) {
  const [showCb, setShowCb] = useState(false);
  const [cbDone, setCbDone] = useState(false);
  const pw = data.pathway || {};

  const book = async () => {
    track("book_click");
    api.post(`/clarity/${data.assessment_id}/book-click`, {}, tokenHeader(token)).catch(() => {});
    if (data.calendar_url) window.open(data.calendar_url, "_blank", "noopener");
  };

  return (
    <section data-testid="report-next-steps" className="border border-exodus-gold bg-exodus-elevated p-6 sm:p-10 relative">
      <div className="absolute top-0 left-0 w-20 h-1 bg-exodus-gold" />
      <p className="eyebrow flex items-center gap-2"><ChatCircleText size={14} /> Suggested professional conversation · {pw.label}</p>
      <h3 className="font-display text-3xl mt-3 mb-4 max-w-2xl">{data.narrative?.closing || "A conversation can help turn these observations into a plan — with no obligation to purchase anything."}</h3>
      <p className="text-sm text-exodus-text/90 max-w-2xl">{pw.message}</p>
      {pw.service_fit === "limited" && (
        <p data-testid="report-service-fit-note" className="text-xs text-exodus-muted mt-3 max-w-2xl">Your current priorities are foundational. Exodus focuses on retirement income, protection, and tax-diversification planning — the free resources below may be the most useful place to start.</p>
      )}
      <div className="grid md:grid-cols-2 gap-8 mt-8">
        <div>
          <p className="eyebrow text-exodus-muted mb-3">Questions you may want to ask</p>
          <ul className="space-y-2 text-sm">{(pw.advisor_questions || []).map((q) => <li key={q} className="flex gap-2"><span className="text-exodus-gold">?</span>{q}</li>)}</ul>
        </div>
        <div>
          <p className="eyebrow text-exodus-muted mb-3">Keep learning</p>
          {pw.resource && (
            <Link to={pw.resource.href} data-testid="report-pathway-resource" className="inline-flex items-center gap-2 text-sm gold-text hover:underline">
              {pw.resource.label} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
      <div className="mt-10 pt-8 border-t border-exodus-border flex flex-wrap gap-4">
        <button data-testid="report-book-call" onClick={book} className="btn-gold"><CalendarCheck size={18} weight="fill" /> Book a free call</button>
        <button data-testid="report-request-callback" onClick={() => (data.contact_captured ? setShowCb((s) => !s) : onNeedContact())} className="btn-ghost">
          <PhoneCall size={16} /> Request a callback
        </button>
      </div>
      {cbDone && <p data-testid="callback-success" className="mt-4 text-sm text-exodus-emerald">Callback requested — you'll hear from Leon in your selected window.</p>}
      {showCb && !cbDone && <CallbackForm assessmentId={data.assessment_id} token={token} onDone={() => { setCbDone(true); setShowCb(false); }} />}
    </section>
  );
}
